// articles/SkinsSetup.js
// Game guide (rewritten Oct 4, 2026 in the Nassau guide's format — Dennis: game guides are
// for a weekly round, with no printing, cart signs or other event steps; a short note at
// the end covers running skins beside an event's main game). First written Sept 29, 2026
// as event setup guide #5.
// Written from the live code: the skins card in Games Setup (App.js renderSkinsCard —
// Basis, Payout, Strokes, Wager / Buy-in, Physical pot settlement), the engine
// (gameEngines/skins.js) and the statement. Button and tab names are quoted exactly as
// they appear, so if a label changes in App.js, change it here too.
//
// Linked from inside the app: 📖 Guides → Game guides (and the events list), and the
// skins card in Games Setup. Public, so prospective organizers can read it first.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-skins-game');

const FAQS = [
  {
    q: 'Off the low man or full course handicap: which should I use?',
    a: 'Either is fair; they differ in who gets the strokes. Off the low man, the lowest handicap in the game plays off scratch and everyone else gets the difference, which is how most weekly groups play. On full course handicap, every player gets all of his strokes. The differences between players are the same either way, but on full course handicap a player’s strokes don’t depend on who else is in, and taking a player out doesn’t move anyone else’s strokes.',
  },
  {
    q: 'What happens to skins still carried over after the last hole?',
    a: 'They aren’t paid. In a carryover game, if the last hole is tied, the skins riding on it go unclaimed and no money changes hands for them. TTO doesn’t play extra holes or split them.',
  },
  {
    q: 'Why isn’t a hole decided yet when my group has finished it?',
    a: 'A skin is decided only once every player in the game has a score on that hole. With several groups in the game, a hole fills in when the last group has played it.',
  },
  {
    q: 'Can I run gross and net skins on the same round?',
    a: 'Yes. Click + Create an additional skins game. A round can have two skins games, and they must differ in basis, payout or stake: gross and net at $5 each, for example. Each player opts into each game separately.',
  },
  {
    q: 'Can I run skins with Captain’s Choice?',
    a: 'No. Skins needs each player’s own score on every hole, and a Captain’s Choice round has one score per team.',
  },
  {
    q: 'We settle up in cash. Can TTO just track the skins?',
    a: 'Yes. Set the wager (or buy-in) to $0. TTO tracks who won which holes and settles no money. Or use the Pot with Physical pot settlement, and the statement shows what each winner takes from the cash.',
  },
  {
    q: 'How does a plus handicap work?',
    a: 'Off the low man, a plus player is usually the low man, and everyone else gets the full difference to him (a +2 against a 10 gives the 10 twelve strokes). On full course handicap, a plus player plays off scratch: TTO doesn’t add strokes back to his score, the same as in every other TTO game.',
  },
];

export default function SkinsSetup() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up a Skins Game
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        In a skins game every hole is a prize. The player with the lowest score on a hole, alone,
        wins the skin; if two or more tie for low, nobody wins it. Every player in the game competes
        against every other on each hole, whatever group he plays in. Skins can be gross or net, and
        paid per hole or from a pot.
      </P>
      <P>
        This guide sets up skins for your weekly round, from the first click to the settlement.
        Running skins beside an event&rsquo;s main game? The steps are the same; see the note at the
        end.
      </P>

      <StepTracker
        storageKey="tto_guide_skins_game_steps"
        steps={[
          'Add the skins game',
          'Who’s in',
          'Play & follow the skins',
          'Results & settle',
        ]}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Skins needs live scoring.</strong> Every player in it needs his scores entered in
          the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Skins isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          Decide with your group:
          <UL>
            <LI><strong>Gross or net</strong>, and on net, strokes <strong>off the low man</strong> or on each player&rsquo;s <strong>full course handicap</strong>.</LI>
            <LI><strong>How the money works:</strong> so much a hole with ties carrying over, so much a hole with ties dropped, or a buy-in pot split by skins won. Or no money, and TTO just tracks the skins.</LI>
            <LI><strong>The stake:</strong> the amount per hole, or each player&rsquo;s buy-in.</LI>
          </UL>
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add the skins game" when="Before the round">
        <Heads>
          <strong>On a net game, every player needs a complete profile.</strong> Each
          player&rsquo;s profile must include a <strong>preferred tee</strong> and a{' '}
          <strong>Handicap Index</strong>, so that his course handicap can be worked out. Check them
          in Admin &rarr; Players. A player with no Handicap Index can&rsquo;t opt himself in, and TTO warns you if you put him in; until he adds one he plays as scratch.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Skins</strong>.
          </LI>
          <LI>
            <strong>Basis:</strong> <Btn>Gross</Btn> (no handicaps; every player plays off scratch)
            or <Btn>Net (handicap)</Btn>.
          </LI>
          <LI>
            <strong>Payout:</strong>
            <UL>
              <LI>
                <Btn>Carryover</Btn>: each hole is worth the wager. A tied hole carries over, so the
                next hole is worth two, and so on until someone wins a hole outright and collects
                every skin riding on it, from each other player. Skins still carried after the last
                hole are not paid.
              </LI>
              <LI>
                <Btn>No carryover</Btn>: each hole is worth the wager. The winner collects it from
                each other player; a tied hole is simply void.
              </LI>
              <LI>
                <Btn>Pot</Btn>: each player puts in a buy-in, and the whole pot is split by the
                number of skins won. Tied holes are void. For example, 8 players at $10 is an $80
                pot; if 5 skins are won, each is worth $16.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>Strokes</strong> (net only):
            <UL>
              <LI>
                <Btn>Off the low man</Btn>: the lowest course handicap in the skins game plays off
                scratch, and every other player gets the difference. A 6 against a low man of 4 gets
                2 strokes. This is the default, and how most weekly groups play.
              </LI>
              <LI>
                <Btn>Full course handicap</Btn>: every player gets all of his own course handicap
                strokes.
              </LI>
            </UL>
            Either way, each player&rsquo;s strokes fall on the hardest holes of his own tee, with a
            second stroke on the hardest holes for a handicap over 18. On a nine-hole round TTO uses
            his 9-hole course handicap.
          </LI>
          <LI>
            <strong>Wager</strong> (Carryover and No carryover): the amount per hole.{' '}
            <strong>Buy-in</strong> (Pot): each player&rsquo;s entry.
          </LI>
          <LI>
            <strong>$0 is a results-only game:</strong> TTO tracks who won which holes and settles no
            money.
          </LI>
          <LI>
            <strong>Physical pot settlement</strong> (Pot only): tick it if players actually put cash
            in a pot, and the statement shows what each winner takes out. Leave it unticked and
            players settle only the differences with one another.
          </LI>
          <LI>
            The green line under the settings describes the game in words. Read it once before you
            save.
          </LI>
          <LI>
            <strong>Gross and net side by side?</strong> Click{' '}
            <Btn>+ Create an additional skins game</Btn>. A round can have two skins games, and they
            must differ in basis, payout or stake.
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            A new skins game starts with nobody in it. After you save, the game shows a{' '}
            <strong>Player Opt-Ins</strong> list with every player on the tee time.
          </LI>
          <LI>
            Click a player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the
            reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app, until the first score of the
            round is entered. After that only you can add or remove a player.
          </LI>
          <LI>
            Only players who are in compete for skins and pay or collect.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play and follow the skins" when="During the round">
        <H3>Keeping score</H3>
        <UL>
          <LI>
            Each player enters his scores hole by hole, or one player keeps the card for his group
            with <strong>+ Score for another player</strong>.
          </LI>
          <LI>
            <strong>Picked up?</strong> Tap <strong>X</strong>. It counts as a net double bogey and
            shows as &ldquo;7x&rdquo;.
          </LI>
        </UL>
        <H3>Following the skins</H3>
        <UL>
          <LI>
            Players follow the game in their app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong>, with the skins each player has won so far and the money.
          </LI>
          <LI>
            <strong>A hole is decided only when every player in the game has a score on it.</strong>{' '}
            With several groups in the game, a hole fills in when the last group has played it.
          </LI>
          <LI>
            Carryovers run in hole-number order, 1 through 18, whatever hole a group started on.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete. You can also click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            <strong>A player who didn&rsquo;t finish?</strong> One unfinished card holds up the holes
            he didn&rsquo;t play for the whole game. When you finalize, TTO lists anyone with an
            incomplete card: wait for his scores, or mark him DNF and the holes he didn&rsquo;t play
            count for everyone else. Removing a player can change other players&rsquo; skins.
          </LI>
          <LI>
            The statement shows how strokes were given, each player&rsquo;s net scorecard with his
            stroke holes marked and the holes he won, the skins each player won including carried
            holes, and the money. On a pot game it also shows the pot and the value of a skin.
          </LI>
          <LI>
            With a wager or buy-in, TTO settles the money player to player, and you can email every
            player his results with one click.
          </LI>
          <LI>
            <strong>A score needs fixing after finalize?</strong> Click <Btn>↶ Reopen Round</Btn> in{' '}
            <Btn>🎮 Games</Btn>, correct it in Admin &rarr; <strong>⛳ Scoring</strong> &rarr;{' '}
            <strong>⛳ Post-Round Score Entry</strong>, then finalize again.
          </LI>
        </UL>
      </Step>

      <H2>Running skins in an event?</H2>
      <P>
        Set the event up with its format guide (📖 Guides &rarr; Setting up Events / Tournaments).
        At the step where you add the main game (Stroke Play, Best Ball or Shamble), tick{' '}
        <strong>Skins</strong> beside it and set it up as in step 1 above. In an event the Pot is the
        usual choice.
      </P>

      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>Players can no longer opt in or out themselves</strong> once the first score of the
          round is entered. You still can, from <Btn>🎮 Games</Btn>.
        </LI>
        <LI>
          <strong>Game settings can still change</strong>, including gross or net and how strokes are
          given, but change them only if you must: the skins and any money follow the new settings.
        </LI>
      </UL>

      <FAQ id="skins-setup" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
